import * as React from 'react';

import { Box, Button, Loader, TextRow, Typography } from 'components';

import { AcsParamsName3DS2, Confirmation3DS, Confirmation3DS2 } from "types";
import { ENDPOINTS } from 'endpoints';
import { TEXT } from 'texts';
import { UrlParam } from "enums";
import { formatAmount } from 'utils';
import { observer } from 'mobx-react-lite';
import { paths } from 'routing';
import { useHistory } from 'react-router';
import { useStores } from 'hooks';
import { useTranslation } from 'react-i18next';
import { IframeForms } from "../../../components";

const TransferConfirmForm = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { dataStore, formStore, appStore, errorStore } = useStores();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [formData, setFormData] = React.useState<Confirmation3DS | Confirmation3DS2 | null>(null);
  const [iframesData, setIframesData] = React.useState<Partial<Confirmation3DS2> | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    if (formData && formRef.current) {
      formRef.current.submit();
    }
  }, [formData]);

  const submit3DsHandler = (response: Confirmation3DS2): void => {
    if (response.ascUrl) {
      const url = new URL(ENDPOINTS.CONFIRM_3DS);

      url.searchParams.append(UrlParam.SPLIT_ID, appStore.id);
      url.searchParams.append(UrlParam.ORDER_ID, appStore.orderId);
      url.searchParams.append(UrlParam.LANG, appStore.lang);
      url.searchParams.append(UrlParam.REDIRECT_URL, window.location.origin);

      dataStore.selectedParticipant &&
      url.searchParams.append(UrlParam.PARTICIPANT_ID, `${dataStore.selectedParticipant.participantId}`);

      const thermUrlParam = { name: 'TermUrl' as AcsParamsName3DS2, value: url.toString() };

      const data: Confirmation3DS2 = {
        ...response,
      };

      data.acsParams.push(thermUrlParam);
      setFormData(data);
    }

    if (response.methodURL || response.methodURLServer) {
      setIframesData({
        methodURL: response.methodURL,
        methodURLServer: response.methodURLServer,
        methodDataPacked: response.methodDataPacked,
        serverTransID: response.serverTransID,
      });
      setIsLoading(true);
    }
  }

  const submit = async () => {
    try {
      setIsLoading(true);
      const response = await formStore.submit();
      setIsLoading(false);

      if (response.orderStatus) {
        const isResponseSuccess = response.orderStatus === 'CONFIRMED' || response.orderStatus === 'PROCESSED';
        history.push(isResponseSuccess ? paths.success : paths.error);
      }

      if (response.confirmationType === '2DS') {
        appStore.setOrderId(response.orderId);
        history.push(paths.lookup);
      }

      if (response.confirmationType === 'OTP' && response.confirmationOTP?.otpSent) {
        appStore.setOrderId(response.orderId);
        history.push(paths.lookup);
      }

      if (response.confirmationType === '3DS' && response.confirmation3DS) {
        const data: Confirmation3DS = {
          ...response.confirmation3DS,
          acsParams: response.confirmation3DS.acsParams.map((param) => {
            if (param.name === 'TermUrl') {
              const url = new URL(ENDPOINTS.CONFIRM_3DS);

              url.searchParams.append(UrlParam.SPLIT_ID, appStore.id);
              url.searchParams.append(UrlParam.ORDER_ID, response.orderId);
              url.searchParams.append(UrlParam.LANG, appStore.lang);
              url.searchParams.append(UrlParam.REDIRECT_URL, window.location.origin);

              dataStore.selectedParticipant &&
                url.searchParams.append(UrlParam.PARTICIPANT_ID, `${dataStore.selectedParticipant.participantId}`);

              return { name: param.name, value: url.toString() };
            }
            return param;
          }),
        };
        setFormData(data);
      }

      if (response.confirmationType === '3DS2' && response.confirmation3DS2) {
        appStore.setOrderId(response.orderId);
        submit3DsHandler(response.confirmation3DS2);
      }
    } catch (error) {
      setIsLoading(false);
      errorStore.setError({ title: TEXT.error, description: (error as Error).message });
    }
  };

  const confirm = async () => {
    try {
      setIsLoading(true);
      const response = await formStore.confirm();

      setIsLoading(false);

      if (response.orderStatus) {
        const isResponseSuccess = response.orderStatus === 'CONFIRMED' || response.orderStatus === 'PROCESSED';

        history.push(isResponseSuccess ? paths.success : paths.error);
      }

      if (response.confirmation3DS2) {
        submit3DsHandler(response.confirmation3DS2);
      }

    } catch (error) {
      setIsLoading(false);
      errorStore.setError({ title: TEXT.error, description: (error as Error).message });
    }
  }

  const handleSubmitIframes = () => {
    setIframesData(null);
    setIsLoading(false);
    confirm();
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  return (
    <>
      {isLoading && <Loader />}
      {formData && (
        <form ref={formRef} action={formData.ascUrl} method="POST">
          {formData.acsParams.map((param: { name: string; value: string }, i: number) => (
            <input hidden readOnly key={i} name={param.name} value={param.value} />
          ))}
        </form>
      )}
      <IframeForms iframesData={iframesData} onIframesSend={handleSubmitIframes}/>
      <form onSubmit={handleSubmit}>
        <Box pt={1}>
          <Typography textType="title" textColor="black" align="center" fontWeight={600}>
            {t(TEXT.transferDetails)}
          </Typography>
        </Box>
        <Box mt={4}>
          <TextRow title={t(TEXT.transferDirection)} subtitle={dataStore.transferData.country} />
        </Box>
        <Box mt={4}>
          <TextRow title={t(TEXT.cityLabel)} subtitle={formStore.city.value} />
        </Box>
        <Box mt={4}>
          <TextRow title={t(TEXT.addressLabel)} subtitle={formStore.address.value} />
        </Box>
        <Box mt={4}>
          <TextRow title={t(TEXT.nameLabel)} subtitle={formStore.name.value} />
        </Box>
        <Box mt={4}>
          <TextRow title={t(TEXT.collectMoney)} subtitle={dataStore.appData?.recipient.purposeText} />
        </Box>
        <Box mt={4}>
          <TextRow title={t(TEXT.amount)} text={formatAmount({ amount: formStore.amount.value }) as string} />
        </Box>
        <Box mt={4}>
          <TextRow
            title={t(TEXT.commission)}
            text={dataStore.transferData.commission}
            subtext={dataStore.transferData.minCommission}
          />
        </Box>
        <Box pt="40px" mt={'auto'}>
          <Button buttonType="round" background="gradient" fullWidth type="submit">
            <Typography textType="button" textColor="white">
              {t(TEXT.send)}
            </Typography>
          </Button>
        </Box>
      </form>
    </>
  );
};

export default observer(TransferConfirmForm);
